import { useEffect, useState, useRef, useCallback } from "react";
import { useAuth } from "@/gestionale/hooks/useAuth";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/gestionale/components/ui/card";
import { Button } from "@/gestionale/components/ui/button";
import { Gamepad2, Trophy, Flame } from "lucide-react";
import BoarHuntGame from "./BoarHuntGame";
import { BoarEventType, getLevelForScore } from "./BoarHuntScene";

const GAME_DURATION = 20;
const MAX_ATTEMPTS = 3;
const MAX_MULTIPLIER_STEPS = 4; // 4 step da 0.5 = fino a +2.0, moltiplicatore massimo x3

export default function Gioco() {
  const { user } = useAuth();
  const [gameState, setGameState] = useState<"idle" | "playing" | "ended">("idle");
  const [score, setScore] = useState(0);
  const [combo, setCombo] = useState(0);
  const [timeLeft, setTimeLeft] = useState(GAME_DURATION);
  const [classifica, setClassifica] = useState<any[]>([]);
  const [attemptsToday, setAttemptsToday] = useState(0);
  const [bestScore, setBestScore] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastEvent, setLastEvent] = useState<BoarEventType | null>(null);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const scoreRef = useRef(0);
  const comboRef = useRef(0);

  const currentMonth = new Date().toISOString().slice(0, 7);

  // Il gestionale usa l'oggetto User grezzo di Supabase (user.id, non
  // user.user_id) e non ha un campo "name" diretto: ricaviamo un nome
  // leggibile dai metadata dell'utente, oppure dalla parte prima della @
  // dell'email come fallback.
  const userName = (user?.user_metadata?.name as string) || user?.email?.split("@")[0] || "Giocatore";

  const loadClassifica = useCallback(async () => {
    const { data } = await supabase
      .from("game_scores")
      .select("user_name, score, played_at")
      .eq("month", currentMonth)
      .order("score", { ascending: false })
      .limit(10);

    const best: Record<string, any> = {};
    (data || []).forEach((r: any) => {
      if (!best[r.user_name] || r.score > best[r.user_name].score) {
        best[r.user_name] = r;
      }
    });
    setClassifica(Object.values(best).sort((a: any, b: any) => b.score - a.score));
  }, [currentMonth]);

  const loadUserStats = useCallback(async () => {
    if (!user?.id) return;
    const today = new Date().toISOString().slice(0, 10);
    const { data } = await supabase
      .from("game_scores")
      .select("score, played_at")
      .eq("user_id", user.id)
      .eq("played_at", today);

    setAttemptsToday((data || []).length);

    const { data: best } = await supabase
      .from("game_scores")
      .select("score")
      .eq("user_id", user.id)
      .eq("month", currentMonth)
      .order("score", { ascending: false })
      .limit(1);

    setBestScore(best?.[0]?.score ?? null);
  }, [user, currentMonth]);

  const loadData = useCallback(async () => {
    setLoading(true);
    await loadClassifica();
    if (user) await loadUserStats();
    setLoading(false);
  }, [user, loadClassifica, loadUserStats]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const startGame = () => {
    if (!user) return;
    if (attemptsToday >= MAX_ATTEMPTS) return;

    scoreRef.current = 0;
    comboRef.current = 0;
    setScore(0);
    setCombo(0);
    setTimeLeft(GAME_DURATION);
    setLastEvent(null);
    setGameState("playing");

    let t = GAME_DURATION;
    timerRef.current = setInterval(() => {
      t--;
      setTimeLeft(t);
      if (t <= 0) {
        if (timerRef.current) clearInterval(timerRef.current);
        endGame();
      }
    }, 1000);
  };

  // Moltiplicatore corrente in base alla serie: x1, x1.5, x2, x2.5, x3 (ogni 3 colpi consecutivi)
  const multiplierForCombo = (c: number) => 1 + Math.min(MAX_MULTIPLIER_STEPS, Math.floor(c / 3)) * 0.5;

  // Chiamata dalla scena Phaser per ogni evento: colpo, colpo dorato, errore (coniglio), fuga
  const handleGameEvent = useCallback((type: BoarEventType) => {
    setLastEvent(type);

    if (type === "hit" || type === "golden") {
      const multiplier = multiplierForCombo(comboRef.current);
      const basePoints = type === "golden" ? 3 : 1;
      const points = Math.round(basePoints * multiplier);
      scoreRef.current += points;
      comboRef.current += 1;
      setScore(scoreRef.current);
      setCombo(comboRef.current);
    } else if (type === "wrong") {
      scoreRef.current = Math.max(0, scoreRef.current - 1);
      comboRef.current = 0;
      setScore(scoreRef.current);
      setCombo(0);
    } else if (type === "escape") {
      comboRef.current = 0;
      setCombo(0);
    }
  }, []);

  const endGame = async () => {
    setGameState("ended");

    if (user) {
      await supabase.from("game_scores").insert({
        user_id: user.id,
        user_name: userName,
        score: scoreRef.current,
        played_at: new Date().toISOString().slice(0, 10),
        month: currentMonth,
      });
      setAttemptsToday((prev) => prev + 1);
      await loadData();
    }
  };

  const timerPercent = (timeLeft / GAME_DURATION) * 100;
  const attemptsLeft = MAX_ATTEMPTS - attemptsToday;
  const mese = new Date().toLocaleDateString("it-IT", { month: "long", year: "numeric" });
  const level = getLevelForScore(score);
  const currentMultiplier = multiplierForCombo(combo);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <Gamepad2 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Acchiappa il Cinghiale 🐗</h1>
          <p className="text-sm text-muted-foreground">
            Tocca i cinghiali, evita i conigli 🐰, punta al cinghiale dorato ✨. Il migliore del mese vince un premio!
          </p>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-between items-center mb-2">
            <div className="text-center">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Punti</div>
              <div className="font-bold text-3xl text-primary">{score}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Tentativi rimasti</div>
              <div className="font-bold text-3xl">{attemptsLeft}</div>
            </div>
            <div className="text-center">
              <div className="text-xs text-muted-foreground uppercase tracking-wide">Tempo</div>
              <div className={`font-bold text-3xl ${timeLeft <= 5 ? "text-destructive" : ""}`}>{timeLeft}</div>
            </div>
          </div>

          {gameState === "playing" && (
            <div className="flex items-center justify-center gap-4 mb-4 text-xs">
              <span className="px-2 py-1 rounded-full bg-primary/10 text-primary font-semibold">Livello {level}</span>
              {combo > 0 && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-orange-500/10 text-orange-500 font-semibold">
                  <Flame className="h-3 w-3" /> Serie {combo} · x{currentMultiplier.toFixed(1)}
                </span>
              )}
            </div>
          )}

          <div className="w-full bg-muted rounded-full h-2 mb-5 overflow-hidden">
            <div
              className={`h-2 rounded-full transition-all duration-1000 ${timeLeft <= 5 ? "bg-destructive" : "bg-primary"}`}
              style={{ width: `${timerPercent}%` }}
            />
          </div>

          {gameState === "playing" && (
            <div className="mb-5">
              <BoarHuntGame active={gameState === "playing"} onEvent={handleGameEvent} />
            </div>
          )}

          {gameState === "idle" &&
            (attemptsLeft > 0 ? (
              <Button onClick={startGame} className="w-full" size="lg">
                Inizia a giocare
              </Button>
            ) : (
              <div className="text-center py-4 text-muted-foreground text-sm">
                Hai esaurito i tentativi di oggi. Torna domani!
              </div>
            ))}

          {gameState === "ended" && (
            <div className="text-center py-2">
              <p className="text-sm mb-1">
                {score < 5
                  ? "Il cinghiale ti ha battuto! 😅"
                  : score < 10
                  ? "Bravo cacciatore! 💪"
                  : score < 15
                  ? "Sei velocissimo! ⚽"
                  : "Sei il re della Val di Susa! 🏆"}
              </p>
              {bestScore !== null && (
                <p className="text-xs text-muted-foreground mb-3">
                  Il tuo record questo mese: <span className="text-primary font-bold">{bestScore}</span>
                </p>
              )}
              {attemptsLeft > 0 && (
                <Button onClick={startGame} className="mt-1">
                  Gioca ancora ({attemptsLeft} rimasti)
                </Button>
              )}
              {attemptsLeft === 0 && (
                <p className="text-muted-foreground text-sm">Hai esaurito i tentativi di oggi. Torna domani!</p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Trophy className="h-4 w-4" /> Classifica {mese}
            </CardTitle>
            <span className="text-xs text-muted-foreground">Miglior punteggio</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="px-5 py-8 text-center text-muted-foreground text-sm">Caricamento...</div>
          ) : classifica.length === 0 ? (
            <div className="px-5 py-8 text-center text-muted-foreground text-sm">Nessun punteggio questo mese. Sii il primo!</div>
          ) : (
            <ul className="divide-y divide-border">
              {classifica.map((r, idx) => (
                <li key={r.user_name} className={`px-5 py-4 flex items-center gap-4 ${idx === 0 ? "bg-primary/5" : ""}`}>
                  <span
                    className={`font-black text-xl w-8 ${
                      idx === 0 ? "text-primary" : idx === 1 ? "text-muted-foreground" : idx === 2 ? "text-amber-600" : "text-muted-foreground/60"
                    }`}
                  >
                    {idx === 0 ? "🥇" : idx === 1 ? "🥈" : idx === 2 ? "🥉" : `${idx + 1}.`}
                  </span>
                  <span className="flex-1 font-semibold">{r.user_name}</span>
                  <span className="font-black text-primary text-xl">{r.score}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}